'use client';

import { CompanyFormData } from '@/shared/lib/validation/registration-wizard/company';
import { SignupCredentialsFormData } from '@/shared/lib/validation/registration-wizard/credentials';
import { ProfileFormData } from '@/shared/lib/validation/registration-wizard/profile';
import { useRef } from 'react';
import { ArrowRightIcon } from '@phosphor-icons/react/dist/ssr';
import { Button, Separator, Text } from '@/shared/ui/core';
import type { FormHandle } from '@/shared/ui/forms/form-handle';
import { RequiredDataNote } from '@/shared/ui/kit/required-data-note';
import {
    RegistrationCard, RegistrationCardContent, RegistrationCardDescription, RegistrationCardHeader,
    RegistrationCardTitle,
} from '@/shared/ui/layout/registration-card';
import { useWizard } from '../../../model/wizard-context';
import { WizardFooter } from '../../wizard-footer';
import { ProfileSubForm } from './profile-sub-form';
import { CompanySubForm } from './company-sub-form';
import { CredentialsSubForm } from './credentials-sub-form';
import styles from './step-profile.module.scss';


export const StepProfile = () => {
    const { state, dispatch } = useWizard();

    const profileRef     = useRef<FormHandle<ProfileFormData>>(null);
    const companyRef     = useRef<FormHandle<CompanyFormData>>(null);
    const credentialsRef = useRef<FormHandle<SignupCredentialsFormData>>(null);


    const handleNext = async () => {
        const results = await Promise.all([
            profileRef.current?.submit(),
            companyRef.current?.submit(),
            credentialsRef.current?.submit(),
        ]);

        if (results.every(Boolean)) {
            dispatch({ type: 'next' });
        }
    };

    return (
        <div data-template="step-profile">
            <RegistrationCard
                data-template="registration-steps-card"
            >
                <RegistrationCardHeader data-template="registration-steps-card-header">
                    <RegistrationCardTitle data-template="registration-steps-card-title">
                        Profile & basic info
                    </RegistrationCardTitle>

                    <RegistrationCardDescription data-template="registration-steps-card-description">
                        Enter your details for prompt registration.
                    </RegistrationCardDescription>
                </RegistrationCardHeader>

                <RegistrationCardContent data-template="registration-steps-card-content">
                    <div className={ styles.root }>
                        <div className={ styles.profile_grid }>
                            <Text
                                as="h3"
                                weight="semibold"
                                lineHeight={ 1.2 }
                                className={ styles.header_slot }
                            >
                                User profile
                            </Text>

                            <ProfileSubForm
                                ref={ profileRef }
                                defaultValues={ state.data.profile }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'profile', value: data }) }
                            />
                        </div>

                        <div className={ styles.company_grid }>
                            <Text
                                as="h3"
                                weight="semibold"
                                lineHeight={ 1.2 }
                                className={ styles.header_slot }
                            >
                                Company information
                            </Text>

                            <CompanySubForm
                                ref={ companyRef }
                                defaultValues={ state.data.company }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'company', value: data }) }
                            />
                        </div>

                        <Separator
                            orientation="vertical"
                            className={ styles.separator }
                        />

                        <div className={ styles.credentials_grid }>
                            <Text
                                as="h3"
                                weight="semibold"
                                lineHeight={ 1.2 }
                                className={ styles.header_slot }
                            >
                                Login credentials
                            </Text>

                            <CredentialsSubForm
                                ref={ credentialsRef }
                                defaultValues={ state.data.credentials ?? { email: state.data.email?.email ?? '' } }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'credentials', value: data }) }
                            />
                        </div>
                    </div>
                </RegistrationCardContent>
            </RegistrationCard>

            <WizardFooter
                state={ state }
                left={
                    <RequiredDataNote className={ styles.required }/>
                }
                right={
                    <Button
                        onClick={ handleNext }
                        leftIcon={ <ArrowRightIcon/> }
                    >
                        Continue
                    </Button>
                }
            />
        </div>
    );
};
